import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {
  connectReferralCodeService,
  getMyReferralLinkService,
  getMyReferralStatsService,
  getMyReferredUsersService,
} from "./referrals.service";
import type {ReferralLink, ReferralStats, ReferredUser} from "./referrals.types";

export const REFERRAL_LINK_QUERY_KEY = ["referral-link"] as const;
export const REFERRAL_STATS_QUERY_KEY = ["referral-stats"] as const;
export const REFERRED_USERS_QUERY_KEY = ["referred-users"] as const;

export const useReferralLink = () => {
  const query = useQuery<ReferralLink | null, Error>({
    queryKey: REFERRAL_LINK_QUERY_KEY,
    queryFn: getMyReferralLinkService,
    staleTime: 60_000,
  });
  return {
    link: query.data?.link ?? "",
    code: query.data?.code ?? "",
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
};

export const useReferralStats = () => {
  const query = useQuery<ReferralStats, Error>({
    queryKey: REFERRAL_STATS_QUERY_KEY,
    queryFn: getMyReferralStatsService,
    staleTime: 60_000,
  });
  return {
    stats: query.data ?? {totalReferrals: 0, totalEarned: 0, pending: 0},
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
  };
};

export const useReferredUsers = () => {
  const query = useQuery<ReferredUser[], Error>({
    queryKey: REFERRED_USERS_QUERY_KEY,
    queryFn: getMyReferredUsersService,
    staleTime: 60_000,
  });
  return {
    users: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
  };
};

export const useConnectReferralCode = () => {
  const queryClient = useQueryClient();
  return useMutation<unknown, Error, string>({
    mutationFn: connectReferralCodeService,
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: REFERRAL_STATS_QUERY_KEY});
      queryClient.invalidateQueries({queryKey: REFERRED_USERS_QUERY_KEY});
    },
  });
};
